import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const HomePage = () => {
  // This would typically come from your data source
  const featuredContent = [
    {
      id: 1,
      title: 'Latest Breakthrough in Transformer Architecture',
      category: 'Research',
      summary: 'Researchers have developed a new transformer architecture that reduces computational complexity while maintaining performance.',
      date: '2024-03-15',
      path: '/research/latest-breakthrough',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center',
      imageAlt: 'AI neural network visualization'
    },
    {
      id: 2,
      title: 'New AI Framework Released',
      category: 'Tools',
      summary: 'A new open-source framework for building and deploying AI models has been released with significant performance improvements.',
      date: '2024-03-14',
      path: '/tools/new-framework',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop&crop=center',
      imageAlt: 'Developer working on code'
    },
    {
      id: 3,
      title: 'Understanding Large Language Models',
      category: 'Blog',
      summary: 'A comprehensive guide to understanding how large language models work and their applications in modern AI.',
      date: '2024-03-13',
      path: '/blog/understanding-llms',
      image: 'https://images.unsplash.com/photo-1655720828018-edd2daec9349?w=400&h=250&fit=crop&crop=center',
      imageAlt: 'AI and machine learning concept'
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI News - Latest Updates in Artificial Intelligence</title>
        <meta name="description" content="Stay updated with the latest news, research, tools, and developments in artificial intelligence." />
        <meta name="keywords" content="AI, artificial intelligence, machine learning, deep learning, AI news" />
        <meta property="og:title" content="AI News - Latest Updates in Artificial Intelligence" />
        <meta property="og:description" content="Stay updated with the latest news, research, tools, and developments in artificial intelligence." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-black-800 to-black-900 bg-primary-black">
        {/* Gradient Background Blurs */}
        <div className="absolute inset-0">
          {/* <div className="absolute top-/10 left-10 w-80 h-80 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full mix-blend-normal filter blur-3xl opacity-50 animate-pulse"></div> */}
          <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mix-blend-normal filter blur-3xl opacity-45 animate-pulse animation-delay-2000"></div>
          {/* <div className="absolute bottom-20 left-20 w-72 h-72 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full mix-blend-normal filter blur-3xl opacity-55 animate-pulse animation-delay-4000"></div> */}
          {/* <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-purple-100 to-blue-100 rounded-full mix-blend-normal filter blur-3xl opacity-10 animate-pulse animation-delay-1000"></div> */}
          {/* <div className="absolute bottom-10 right-20 w-64 h-64 bg-gradient-to-r from-cyan-300 to-purple-400 rounded-full mix-blend-normal filter blur-3xl opacity-50 animate-pulse animation-delay-3000"></div> */}
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                The Future of AI
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Starts Here
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Stay ahead of the curve with breaking news, insights, and analysis from the world of artificial intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/research" 
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Explore Latest News
              </Link>
              <Link 
                to="/blog" 
                className="border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-purple-600 hover:text-white transition-all duration-300 transform hover:scale-105"
              >
                Subscribe to Updates
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-gray-100">Featured Content</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredContent.map((item) => (
              <article key={item.id} className="card hover:shadow-xl transition-shadow overflow-hidden">
                <div className="mb-4">
                  <img 
                    src={item.image} 
                    alt={item.imageAlt}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-secondary-blue font-semibold">{item.category}</span>
                    <span className="text-gray-400 text-sm">{item.date}</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-100">{item.title}</h3>
                <p className="text-gray-300 mb-4">{item.summary}</p>
                <Link
                  to={item.path}
                  className="text-secondary-blue font-semibold hover:underline"
                >
                  Read More →
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-gray-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4 text-gray-100">Stay Updated</h2>
            <p className="text-gray-300 mb-8">
              Subscribe to our newsletter for the latest AI news and updates
            </p>
            <form className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 rounded border border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary-blue focus:border-transparent"
                />
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Subscribe
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage; 